import { Component, OnDestroy, OnInit } from '@angular/core';
import { EstablishmentService } from '../../establishment.service';
import { FeiColumn } from '../../../../../../core/base/interfaces/fei-column.interface';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { scaleIn400ms } from '../../../../../../../@vex/animations/scale-in.animation';
import { fadeInRight400ms } from '../../../../../../../@vex/animations/fade-in-right.animation';
import { stagger40ms } from '../../../../../../../@vex/animations/stagger.animation';
import { fadeInUp400ms } from '../../../../../../../@vex/animations/fade-in-up.animation';
import { scaleFadeIn400ms } from '../../../../../../../@vex/animations/scale-fade-in.animation';
import { ENUM_PERMISSIONS } from '../../../../../../core/enums/permission.enum';
import { BuildingModel } from '../../../../../../core/models/building.model';
import { DataTableBase } from '../../../../../../core/base/components/data-table-base/data-table-base';
import { BuildingService } from '../../../building/building.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { EstablishmentModel } from '../../../../../../core/models/establishment.model';
import { Sort } from '@angular/material/sort';
import { takeUntil, take } from 'rxjs/operators';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { SnackbarService } from '../../../../../../core/services/snackbar.service';
import { Router } from '@angular/router';
import { BuildingUnlinkWarningComponent } from '../../modals/building-unlink-warning/building-unlink-warning.component';
import { CrudModeEnum } from '../../../../../../core/base/enum/crud-mode.enum';
import { EstablishmentBuildingCreateUpdateComponent } from '../../modals/establishment-building-create-update/establishment-building-create-update.component';
import { BuildingCreateUpdateDeleteComponent } from '../../../building/modals/building-create-update-delete/building-create-update-delete.component';
import { AuthService } from '../../../../../../core/services/auth.service';

@Component({
  selector: 'vex-establishment-details-activite',
  templateUrl: './establishment-details-buildings.component.html',
  styleUrls: ['./establishment-details-buildings.component.scss'],
  animations: [scaleIn400ms, fadeInRight400ms, stagger40ms, fadeInUp400ms, scaleFadeIn400ms],
})
export class EstablishmentDetailsBuildingsComponent
  extends DataTableBase<EstablishmentModel>
  implements OnInit, OnDestroy
{
  displayedColumns = ['actions'];
  count = 0;
  sortField: string;
  sortOrder: string;
  pageIndex: number;
  pageSize: number;
  search: string;

  feisColumns: Array<FeiColumn> = [
    { label: '', column: 'attentionRequiered', type: 'custom', canSort: false },
    { label: 'Principal', column: 'favorite', canSort: false, canFavorite: false },
    { label: 'Identifiant technique', column: 'technicalIdentifier', propertyDisplay: null },
    { label: 'Dénomination', column: 'label', propertyDisplay: null },
    { label: 'Bâtiment générique', column: 'genericBuilding', propertyDisplay: 'genericBuilding.label' },
    {
      label: 'Catégorie de bâtiment',
      column: 'genericBuildingCategory',
      propertyDisplay: 'genericBuildingCategory.label',
    },
    { label: 'Surface total (m²)', column: 'surface', propertyDisplay: null, type: 'number', maxDecimal: 2 },
    { label: 'Espace occupé (m²)', column: 'occupiedSpace', propertyDisplay: null, type: 'number', maxDecimal: 2 },
    { label: 'Type de détention', column: 'detentionType', propertyDisplay: 'detentionType.label' },
    {
      label: '',
      column: 'actions',
      showAction: this.authService.hasPermission(ENUM_PERMISSIONS.UPDATE_BUILDING),
      canUpdate: false,
      canDelete: false,
    },
  ];

  loading = false;
  entityName = 'Bâtiments';
  ENUM_PERMISSIONS = ENUM_PERMISSIONS;
  filters: any = {};
  establishment: EstablishmentModel;
  protected _building = new BehaviorSubject<BuildingModel[]>(null);
  readonly building$ = this._building.asObservable();
  hasPermission: boolean;
  private destroy$ = new Subject<void>();

  constructor(
    public service: EstablishmentService,
    public buildingService: BuildingService,
    private dialog: MatDialog,
    private snackbarService: SnackbarService,
    private router: Router,
    public authService: AuthService,
  ) {
    super();
    this.displayedColumns = this.feisColumns.map(x => x.column);
    this.hasPermission = this.authService.getCurrentUserPermissions().includes(ENUM_PERMISSIONS.UPDATE_BUILDING);
  }

  ngOnInit(): void {
    this.service.entity$.pipe(takeUntil(this.destroy$)).subscribe(entity => {
      if (!entity) {
        return;
      }
      this.establishment = entity as EstablishmentModel;
      this.initData();
    });
  }

  initData() {
    this.loading = true;

    this.filters = {
      establishmentId: this.establishment._id,
      isStatusNeeded: true,
      requiredFields: ['*'],
    };

    this.loadBuildings();
  }

  loadBuildings() {
    this.buildingService
      .findAll(this.pageIndex, null, this.sortField, this.sortOrder, null, this.filters)
      .subscribe(res => {
        const buildings = res.data.map(data => ({
          ...data,
          favorite: this.establishment.mainBuilding?._id === data._id,
          occupiedSpace: this.calculateOccupiedSpaceInBuilding(data),
        }));

        const buildingIds = buildings.map(b => b._id);
        if (buildingIds.length > 0) {
          const sub = this.buildingService.addressService
            .findAll(1, buildingIds.length * 5, '_id', 'asc', undefined, {
              buildingIds,
              requiredFields: ['buildingIds'],
            })
            .subscribe(addrRes => {
              const withAddress = new Set<string>();
              addrRes.data.forEach(addr => addr.buildingIds?.forEach(id => withAddress.add(id)));
              buildings.forEach(b => {
                if (!withAddress.has(b._id)) {
                  b.isAttentionRequired = true;
                  b.attentionRequieredTooltip = 'Aucune adresse associée';
                }
              });
              this.dataSource = new MatTableDataSource<any>(buildings);
            });
          this.subscription.add(sub);
        } else {
          this.dataSource = new MatTableDataSource<any>(buildings);
        }
        this.count = res.totalData;
        this.loading = false;
      });
  }

  /**
   * Calcule l'espace occupé par l'établissement actuel dans un bâtiment donné
   */
  private calculateOccupiedSpaceInBuilding(building: any): number {
    if (!building?.establishments || !Array.isArray(building.establishments) || !this.establishment?._id) {
      return 0;
    }

    // Chercher l'établissement actuel dans la liste des établissements du bâtiment
    const establishmentInBuilding = building.establishments.find(
      (estab: any) =>
        estab._id === this.establishment._id || estab._id?.toString() === this.establishment._id?.toString(),
    );

    return establishmentInBuilding?.surface || 0;
  }

  async addBuilding() {
    const element = await this.service.entity;

    this.dialog
      .open(EstablishmentBuildingCreateUpdateComponent, {
        data: {
          defaults: element,
          mode: CrudModeEnum.Create,
        },
        width: '600px',
      })
      .afterClosed()
      .subscribe((success: boolean) => {
        if (success) {
          // Recharger immédiatement la liste des bâtiments
          this.loadBuildings();

          // Le back pose le bâtiment et l'adresse principale au rattachement : recharger l'établissement
          // pour rafraîchir les cartes qui en dépendent (Localisations)
          const hadMainAddress = !!this.establishment.mainAddressId;
          this.service
            .findOneById(this.establishment._id)
            .pipe(take(1))
            .subscribe(res => {
              if (!hadMainAddress && res.data?.mainAddressId) {
                this.snackbarService.success('Adresse principale définie automatiquement');
              }
            });
        }
      });
  }

  async updateBuilding(element: BuildingModel) {
    this.dialog
      .open(BuildingCreateUpdateDeleteComponent, {
        data: {
          defaults: element,
          establishment: this.establishment,
          mode: this.hasPermission ? CrudModeEnum.Update : CrudModeEnum.Read,
        },
      })
      .afterClosed()
      .subscribe((supplier: BuildingModel) => {
        if (supplier) {
          this.loadBuildings();
        } else {
          this.dialog.closeAll();
        }
      });
  }

  sortEvent(event: Sort): void {
    this.sortField = event.active;
    this.sortOrder = event.direction;
    this.initData();
  }

  unlinkBuilding(element: BuildingModel) {
    if (this.establishment.mainBuilding?._id === element._id) {
      this.snackbarService.danger('Veuillez modifier le bâtiment favori avant de le délier.');
      return;
    }

    const activities =
      this.establishment.activities?.filter(act =>
        element.addresses?.some(addr => act.addresses?.some(a => a.addressData?._id === addr._id)),
      ) || [];

    if (activities.length) {
      this.dialog.open(BuildingUnlinkWarningComponent, { data: { activities } });
      return;
    }

    this.buildingService
      .deleteEstablishment(element._id, this.establishment._id)
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.service
            .findById(this.establishment._id)
            .pipe(take(1))
            .subscribe(res => {
              this.establishment = res.data;
              this.snackbarService.success('Le bâtiment a été délié');
              this.loadBuildings();
            });
        },
        error: err => {
          const message = err?.error?.message || 'Une erreur est survenue';
          this.snackbarService.danger(message);
        },
      });
  }

  navigateToBuildingDetail($event: any) {
    console.log('ça vient ici ? ');
    this.router.navigateByUrl('/management/building/' + $event._id).then();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    super.ngOnDestroy();
  }
}
